/**
 * External dependencies.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import retina from 'retinajs';

/**
 * Image Component.
 *
 * @param {Object} props Component props.
 *
 * @return {jsx}
 */
const Image = ( props ) => {
	const { altText, title, mediaDetails, sourceUrl, srcSet, className, lazy = true, placeholder = {} } = props;

	if ( ! sourceUrl ) {
		return null;
	}

	useEffect( () => retina(), [] ); // eslint-disable-line

	const attributes = {
		srcSet,
		alt: ! isEmpty( altText ) ? altText : title,
		height: ! isEmpty( mediaDetails ) ? mediaDetails.height : 'auto',
		width: ! isEmpty( mediaDetails ) ? mediaDetails.width : 'auto',
		src: sourceUrl,
		className
	};

	const placeholderSrc = get( placeholder, 'sourceUrl' ) || '';

	if ( placeholderSrc ) {
		attributes.placeholder = (
			<img
				width={ attributes.width }
				height={ attributes.height }
				className="lazy-placeholder-image"
				src={ placeholderSrc }
				alt={ `${ altText || title } placeholder` }
			/> );
	}

	return lazy ? (
		<LazyLoadImage
			{ ...attributes }
			effect="blur"
			data-rjs="2"
			beforeLoad={ retina }
		/>
	) : (
		/**
		 * 1. Empty alt attribute to fix ESLint error, though it will be overridden by attributes.alt
		 * 2. Mostly svg images will be rendered using regular img tag hence retina wasn't used with here.
		 */
		<img alt="" { ...attributes } data-rjs="2" />
	)
};

Image.propTypes = {
	altText: PropTypes.string,
	title: PropTypes.string,
	mediaDetails: PropTypes.object,
	sizes: PropTypes.string,
	sourceUrl: PropTypes.string,
	srcSet: PropTypes.string,
	lazy: PropTypes.bool,
	placeholder: PropTypes.object,
};

Image.defaultProps = {
	altText: '',
	title: '',
	mediaDetails: {
		height: '',
		width: ''
	},
	lazy: true,
	sizes: '',
	sourceUrl: '',
	srcSet: '',
	className: 'article-card__image',
};

export default Image;
